import type { DurationLikeObject, DurationUnit } from 'luxon';
import { DateTime, Duration } from 'luxon';
import type { DateTimeUnit } from 'luxon/src/datetime';
import { ETimeFormat, dateTimeHelper } from '../helpers';

export interface DurationOption {
  hours: number;
  days: number;
  minutes: number;
}
class DateTimeFacade {
  public isBetweenFromISO(date: string, start: string, end: string) {
    const dateTime = dateTimeHelper.getDateFromISO(date);
    const startTime = dateTimeHelper.getDateFromISO(start);
    const endTime = dateTimeHelper.getDateFromISO(end);
    return dateTime.toMillis() > startTime.toMillis() && dateTime.toMillis() < endTime.toMillis();
  }

  public mergeDateTime(date: string, time: string) {
    return dateTimeHelper.dt.fromISO(date).set({
      hour: +dateTimeHelper.getDateFromTime(time).toFormat('HH'),
      minute: +dateTimeHelper.getDateFromTime(time).toFormat('mm'),
    });
  }

  public getFormatFromISO(date: string, format: string, setZone = false) {
    return dateTimeHelper.dt.fromISO(date, { setZone }).toFormat(format);
  }

  public timeToNearest(min = 15) {
    return DateTime.now().plus({
      minute: min - (DateTime.now().minute % min),
      second: 0,
    });
  }

  public startOf(unit: DateTimeUnit) {
    return dateTimeHelper.dt.now().startOf(unit);
  }

  public endOf(unit: DateTimeUnit) {
    return dateTimeHelper.dt.now().endOf(unit);
  }

  public weekStartDate(page: number, isoWeek = false) {
    if (!isoWeek) {
      return dateTimeHelper.dt
        .now()
        .plus({ week: page * -1 })
        .startOf('week')
        .minus({ day: 1 });
    }
    return dateTimeHelper.dt.now().plus({ week: page }).startOf('week');
  }

  public weekEndDate(page: number, isoWeek = false) {
    if (!isoWeek) {
      return dateTimeHelper.dt
        .now()
        .plus({ week: page * -1 })
        .endOf('week')
        .minus({ day: 1 });
    }
    return dateTimeHelper.dt.now().plus({ week: page }).endOf('week');
  }

  public getDiff(start: DateTime, end: DateTime, unit: DurationUnit = 'minutes') {
    return start.diff(end).as(unit);
  }

  getWeekDiffFromISO(start: string, end: string, isoWeek = false) {
    const startOfWeek = dateTimeHelper
      .getDateFromISO(start)
      .startOf('week')
      .minus({ day: !isoWeek ? 1 : 0 });
    const startOfNextWeek = dateTimeHelper
      .getDateFromISO(end)
      .startOf('week')
      .minus({ day: !isoWeek ? 1 : 0 });
    return startOfNextWeek.diff(startOfWeek).as('week');
  }

  public getTimeDiff(start: string, end: string, unit: DurationUnit = 'minutes') {
    return dateTimeHelper.getDateFromTime(start).diff(dateTimeHelper.getDateFromTime(end)).as(unit);
  }

  public getDiffFromISO(start: string, end: string, unit: DurationUnit = 'minute') {
    return dateTimeHelper.dt.fromISO(start).diff(dateTimeHelper.dt.fromISO(end)).as(unit);
  }

  public getNext15minTime(time: string) {
    return dateTimeHelper.getDateFromTime(time).plus({ minute: 14 }).toFormat(ETimeFormat.TIME_24);
  }

  public getNextDayDuration(start: string, end: string) {
    return dateTimeHelper
      .getDateFromTime(start)
      .plus({ day: 1 })
      .diff(dateTimeHelper.getDateFromTime(end))
      .as('minute');
  }

  public formatToExplain(date: string, format: string) {
    return dateTimeHelper.dt.fromFormatExplain(date, format).result;
  }

  public decimalToDuration(fromObj: DurationLikeObject) {
    return Duration.fromObject(fromObj).toFormat('hh:mm:ss');
  }

  public timeToDuration(time: string, unit: DurationUnit = 'minute') {
    // Handle format like '1 00:00:00' (days and time)
    if (time.includes(' ')) {
      const [daysStr, timeStr] = time.split(' ');
      const days = Number(daysStr);
      // Parse the time part as ISO time
      const duration = Duration.fromISOTime(timeStr);
      // Add days to the duration
      return duration.plus({ days }).as(unit);
    }
    // Standard ISO time string
    return Duration.fromISOTime(time).as(unit);
  }

  public dateToDuration(date: string, unit: DurationUnit = 'hour') {
    return this.timeToDuration(dateTimeHelper.getTime(date, 24, true), unit);
  }

  public durationToDate(duration: number, toFormat = 'HH:mm:ss') {
    return this.startOf('day').plus({ minute: duration }).toFormat(toFormat);
  }

  public friendlyDurationName(duration: string): Partial<DurationOption> {
    // Step 1: Parse input into days and time string
    let days = 0;
    let timeStr = duration;
    if (duration.includes(' ')) {
      const [daysStr, tStr] = duration.split(' ');
      days = Number(daysStr);
      timeStr = tStr;
    }

    // Step 2: Convert time string to minutes
    const timeMinutes = this.timeToDuration(timeStr, 'minute');
    const totalMinutes = days * 24 * 60 + timeMinutes;

    // Step 3: Break totalMinutes into days, hours, minutes
    const outDays = Math.floor(totalMinutes / (24 * 60));
    const outHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const outMinutes = Math.round(totalMinutes % 60);

    if (outDays > 0 && totalMinutes > 60) {
      if (outHours === 0 && outMinutes === 0) {
        return { days: outDays, hours: 0 };
      }
      if (outHours === 0) {
        return { days: outDays, minutes: outMinutes };
      }
      if (outMinutes === 0) {
        return { days: outDays, hours: outHours };
      }
      // outDays, outHours, outMinutes all > 0
      return { days: outDays, hours: outHours, minutes: outMinutes };
    }

    if (outHours > 0 && totalMinutes > 60) {
      if (outMinutes === 0) {
        return { hours: outHours };
      }
      return { hours: outHours, minutes: outMinutes };
    }

    // Fallback: should never be reached, but ensures a string is always returned
    return { minutes: totalMinutes };
  }
}

export const dateTimeFacade = new DateTimeFacade();
